import { TDataLayerStyle } from '@lifk/types'
import { groupMulti, isNumber, roundToSigDigits } from '@lifk/utils'
import { ExpressionSpecification } from 'mapbox-gl'

export const getDefaultOpacity = (style: TDataLayerStyle) => {
  switch (style) {
    case 'Lines':
    case 'Points':
      return 1
    default:
      return 0.6
  }
}

const getDynamicOpacityFor = (
  opacity: number | string | undefined, // undefined falls back to default opacity
  style: TDataLayerStyle
): ExpressionSpecification | number => {
  const typeSafeOpacity = isNumber(opacity)
    ? (opacity as number)
    : getDefaultOpacity(style)

  switch (style) {
    case 'Polygons':
    case 'Polygons (symbolized)': {
      // Polygon fills get more opacity on hover
      let hovered = typeSafeOpacity + 0.2
      if (hovered > 1) {
        hovered = 1
      } else {
        hovered = roundToSigDigits(hovered, 2)
      }
      return [
        'case',
        [
          'any',
          ['boolean', ['feature-state', 'active'], false],
          ['boolean', ['feature-state', 'hovered'], false]
        ],
        hovered,
        typeSafeOpacity
      ]
    }
    case 'Points': {
      // Points get more transparent on hover by default
      let hovered = 0.25
      if (hovered >= typeSafeOpacity) {
        // if the point is already significantly transparent, add opacity instead
        hovered = roundToSigDigits(typeSafeOpacity + 0.2, 2)
      }
      return [
        'case',
        [
          'any',
          ['boolean', ['feature-state', 'active'], false],
          ['boolean', ['feature-state', 'hovered'], false]
        ],
        hovered,
        typeSafeOpacity
      ]
    }
    default:
      // lines and rasters are static opacity
      return typeSafeOpacity
  }
}

const getDefaultDynamicOpacity = (style: TDataLayerStyle) =>
  getDynamicOpacityFor(undefined, style)

interface IOpacityConfig {
  style: TDataLayerStyle // we can assume this is the same for all layer values in the list
  filter?: ExpressionSpecification
  opacity?: number
  minValue?: number
  maxValue?: number
  targetAttribute?: string
}

export const getMapboxOpacityExpression = (
  config: IOpacityConfig[],
  opacityByFilter: boolean,
  isGradient: boolean
): ExpressionSpecification | number => {
  if (!config.length) return 1

  const style = config[0].style
  const defaultOpacity = getDefaultDynamicOpacity(style)
  const groupByOpacity = groupMulti(config, ({ opacity }) => [
    isNumber(opacity) ? `${opacity}` : 'default'
  ])

  const opacities = Object.keys(groupByOpacity)
  if (opacities.length === 0) {
    return defaultOpacity
  }

  if (opacities.length === 1) {
    if (opacities[0] === 'default') {
      return defaultOpacity
    } else {
      return getDynamicOpacityFor(
        groupByOpacity[opacities[0]][0].opacity,
        style
      )
    }
  }

  if (opacities.length > 1) {
    return getOpacityCaseStatement(config, opacityByFilter, isGradient)
  }

  // default (we should never get here)
  return defaultOpacity
}

const getOpacityCaseStatement = (
  config: IOpacityConfig[],
  opacityByFilter: boolean,
  isGradient: boolean
): ExpressionSpecification | number => {
  if (opacityByFilter) {
    return getCategoricalOpacity(config)
  } else if (
    config.every(
      ({ minValue, maxValue, targetAttribute }) =>
        isNumber(minValue) &&
        isNumber(maxValue) &&
        targetAttribute &&
        targetAttribute.length > 0
    )
  ) {
    if (isGradient) {
      return getGradientOpacity(config)
    } else {
      return getBinnedOpacity(config)
    }
  }

  // default
  return getDefaultDynamicOpacity(config[0].style)
}

const getCategoricalOpacity = (config: IOpacityConfig[]) => {
  const mapboxOpacityExpression: ExpressionSpecification = ['case']
  config.forEach(({ filter, opacity, style }) => {
    mapboxOpacityExpression.push(filter)
    mapboxOpacityExpression.push(getDynamicOpacityFor(opacity, style))
  })
  mapboxOpacityExpression.push(getDefaultDynamicOpacity(config[0].style))
  return mapboxOpacityExpression
}

const getGradientOpacity = (config: IOpacityConfig[]) => {
  const interpolateExpr: ExpressionSpecification = [
    'interpolate',
    ['linear'],
    ['get', config[0].targetAttribute]
  ]
  config.forEach(({ maxValue, opacity, style }) => {
    interpolateExpr.push(maxValue)
    interpolateExpr.push(getDynamicOpacityFor(opacity, style))
  })
  return interpolateExpr
}

const getBinnedOpacity = (config: IOpacityConfig[]) => {
  const caseStatement: ExpressionSpecification = [
    'case',
    ['<=', ['get', config[0].targetAttribute], 0],
    0
  ]
  config.forEach(({ maxValue, targetAttribute, opacity, style }, index) => {
    // don't add filter for last item (use last color as case statement default)
    if (maxValue && index + 1 < config.length) {
      caseStatement.push(['<', ['get', targetAttribute], maxValue])
    }
    caseStatement.push(getDynamicOpacityFor(opacity, style))
  })
  return caseStatement
}
